// /src/utils/accessControlUtils.js

import { ACLs } from "./appUtils";
import { findObjectByProperty, getRandomInteger } from "./dataUtils";
import { debugLog } from "./debugUtils";
import { isAdmin, isDeveloper, isOwner, isUserInGroup, isUserLogin } from "./sessionUtils";

export const GROUP_PUBLIC = -100;
export const GROUP_ALL_LOGIN = -99;

/**
 * updateAccessArray - Handles add/archive/restore logic for access control arrays 
 * (e.g. baseline_accesses, scenario_accesses) following the RAVEN "soft delete" pattern.
 * - Archives removed accesses (sets update_flag: 103).
 * - Adds new accesses with update_flag: 101, assigns new id, timestamps, and user info.
 * - Restores accesses if re-selected (resets update_flag).
 * Used by Baseline and Scenario modules for consistent permission/role management.

 * @param {Array} existing      - Existing access entries array.
 * @param {Array} selected      - Currently selected access objects (from UI).
 * @param {number|string} cfgCategoryId - Category/type id for filtering.
 * @param {number|string} parentId      - Parent record id (baseline or scenario).
 * @param {number|string} userId        - User id for updatedBy field.
 * @param {string} parentIdKey     - Name of parent key: "baseline_id", "scenario_id", etc.
 * @returns {Array}             - New array reflecting adds, removals, restores.
 */
export const updateAccessArray = (
	existing = [],
	selected = [],
	cfgCategoryId,
	parentId,
	userId,
	parentIdKey = "baseline_id" // default is baseline_id for backward compatibility
) => {
	// Build a quick list of all selected ids (from UI)
	const arrayIds = selected.map((x) => x.id);
	let arrayTemp = [];

	// First: keep or archive existing accesses
	existing.forEach((x) => {
		if (
			(x.comp_type_id === cfgCategoryId && arrayIds.includes(x.comp_value)) ||
			x.comp_type_id !== cfgCategoryId
		) {
			// Keep: still selected, or not part of this config category
			arrayTemp.push(x);
		} else {
			// Archive: no longer selected, so mark as removed (soft delete)
			arrayTemp.push({ ...x, update_flag: 103 });
		}
	});

	// Second: add new, or restore archived accesses for selected items
	selected.forEach((x) => {
		// If this selection doesn't already exist for this config category, add as new
		if (!arrayTemp.find((y) => y.comp_type_id === cfgCategoryId && y.comp_value === x.id)) {
			arrayTemp.push({
				id: getRandomInteger(1000000000, 2000000000), // Assign a new random id
				[parentIdKey]: parentId, 												// Or scenario_id, depending on context
				comp_type_id: cfgCategoryId,
				comp_value: x.id,
				update_flag: 101,	// Mark as new
				updatedAt: new Date(),
				updatedBy: userId,
				new: true,
			});
		} else {
			// If restoring (was archived), reset update_flag accordingly
			arrayTemp.forEach((y) => {
				if (y.comp_value === x.id && y.update_flag === 103) {
					y.update_flag = y.new ? 101 : 0;
				}
			});
		}
	});

	return arrayTemp;
};

/**
 * Compute the frontend access level for a Baseline or Scenario from its ACL rows.
 *
 * ACL comp_type_id values (defined in appUtils.ACLs):
 *   201 = group edit, 202 = user edit, 203 = group read, 204 = user read.
 * The resulting level is 2 (edit), 1 (read), or 0 (no ACL access).
 * comp_value is the user ID for 202/204 and the group ID for 201/203.
 * The special group IDs -100 (PUBLIC) and -99 (ALL_LOGIN) mean everyone and
 * signed-in users, respectively; their access level still comes from the ACL type.
 * Admins, developers, and the resource owner receive edit access before ACL rows
 * are evaluated. This helper evaluates the rows it is given; it does not filter
 * by status_id. Backend authorization is enforced separately.
 *
 * Baseline and Scenario wrappers pass their respective access-row arrays and
 * preserve their existing module-level APIs.
 */
export const evaluateAccessLevel = (
	item,
	accessRows,
	groups,
	user,
	{
		publicLogMessage = "[resourceAccess] PUBLIC -> READ",
		allLoginLogMessage = "[resourceAccess] ALL_LOGIN",
		logIdKey = "resourceId",
		resourceIdKey = "id",
	} = {}
) => {
	const loggedIn = isUserLogin();

	// Preserve the original short-circuit order from the module-local helpers.
	if (isAdmin() || isDeveloper()) return 2;
	if (user?.id != null && isOwner(item, user.id)) return 2;
	if (!Array.isArray(accessRows)) return 0;

	let access = 0;

	accessRows.forEach((row) => {
		const acl = findObjectByProperty(ACLs, "id", row.comp_type_id);
		if (!acl) return;

		// Direct user ACL rows are evaluated before any group-based access.
		if ((row.comp_type_id === 202 || row.comp_type_id === 204) && user?.id != null) {
			if (row.comp_value === user.id) {
				access = Math.max(access, acl.value);
				return;
			}
		}

		const groupId = row.comp_value;
		const resourceId = item?.[resourceIdKey];

		// Special pseudo-groups bypass group lookup and keep module-specific logging unchanged.
		if (groupId === GROUP_PUBLIC) {
			debugLog?.(4, publicLogMessage, { [logIdKey]: resourceId });
			access = Math.max(access, acl.value);
			return;
		}

		if (groupId === GROUP_ALL_LOGIN) {
			debugLog?.(4, allLoginLogMessage, { [logIdKey]: resourceId, loggedIn });
			if (loggedIn) access = Math.max(access, acl.value);
			return;
		}

		// Real groups still flow through the existing lookup + membership path.
		if (groups && user?.id != null) {
			const thisGroup = findObjectByProperty(groups, "id", groupId);
			if (thisGroup && isUserInGroup(thisGroup, user.id)) {
				access = Math.max(access, acl.value);
			}
		}
	});

	return access;
};
